import { create } from 'zustand';
import {
  ChatStore,
  ChatMessage,
  SuggestedQuestion,
  StreamEvent,
} from '../types/chat';
import { chatService } from '../services/chatService';
import { authService } from '../services/authService';
import { getActiveTenantId } from '../services/tenantContext';

/**
 * Chat store using Zustand
 * Manages global AI chat panel state and streaming interactions
 *
 * Requirements:
 * - 1.2: Panel open/close state
 * - 7.2: Session persistence across page navigations
 * - 8.1: SSE streaming token reception
 * - 8.5: Input disabled during streaming
 */

const CHAT_API_BASE =
  import.meta.env.VITE_CHAT_API_BASE_URL || 'http://localhost:8085/api/v1/chat';

/**
 * Generate a unique ID for messages
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Default suggested questions shown on new chats
 * Requirement 5.3: Cover each supported intent category
 */
const DEFAULT_SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
  {
    id: 'sq-1',
    text: 'What is my total cloud spend this month?',
    category: 'cost_overview',
    intent: 'cost_summary',
  },
  {
    id: 'sq-2',
    text: 'Break down costs by service for AWS',
    category: 'cost_overview',
    intent: 'cost_breakdown',
  },
  {
    id: 'sq-3',
    text: 'Are there any cost anomalies this week?',
    category: 'anomalies',
    intent: 'anomaly_detection',
  },
  {
    id: 'sq-4',
    text: 'What recommendations do I have to reduce costs?',
    category: 'optimization',
    intent: 'recommendations',
  },
  {
    id: 'sq-5',
    text: 'What is my projected spend for next month?',
    category: 'forecasts',
    intent: 'forecast',
  },
  {
    id: 'sq-6',
    text: 'Compare AWS vs Azure costs this quarter',
    category: 'cost_overview',
    intent: 'provider_comparison',
  },
  {
    id: 'sq-7',
    text: 'How many accounts do I have onboarded?',
    category: 'accounts',
    intent: 'account_inventory',
  },
  {
    id: 'sq-8',
    text: 'What do I need to onboard an AWS account?',
    category: 'setup',
    intent: 'onboarding_help',
  },
];

/**
 * Session storage keys for persisting chat state across page navigations
 * Requirement 7.2: Chat history persists across page navigations within same browser session
 */
const SESSION_STORAGE_KEY = 'finops-chat-session-id';
const SESSION_MESSAGES_KEY = 'finops-chat-messages';

/**
 * Persist messages to sessionStorage for browser-session persistence
 * Requirement 7.2: Retain conversation state without requiring new session
 */
function persistMessagesToSessionStorage(messages: ChatMessage[]): void {
  try {
    sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(messages));
  } catch {
    // sessionStorage may not be available or quota exceeded
  }
}

/**
 * Load messages from sessionStorage
 * Requirement 7.2: Load session history when panel reopens
 */
function loadMessagesFromSessionStorage(): ChatMessage[] | null {
  try {
    const stored = sessionStorage.getItem(SESSION_MESSAGES_KEY);
    if (stored) {
      return JSON.parse(stored) as ChatMessage[];
    }
  } catch {
    // sessionStorage may not be available or parse error
  }
  return null;
}

/**
 * Parse an SSE line into a StreamEvent object.
 * SSE format: "event: <type>\ndata: <json>"
 */
function parseSSEEvent(eventType: string, data: string): StreamEvent | null {
  try {
    const parsed = JSON.parse(data);
    switch (eventType) {
      case 'token':
        return { type: 'token', content: parsed.content };
      case 'metadata':
        return {
          type: 'metadata',
          intent: parsed.intent,
          endpointsQueried: parsed.endpointsQueried,
          confidenceScore: parsed.confidenceScore,
          timeRange: parsed.timeRange,
        };
      case 'done':
        return { type: 'done', sessionId: parsed.sessionId, messageId: parsed.messageId };
      case 'error':
        return { type: 'error', code: parsed.code, message: parsed.message };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export const useChatStore = create<ChatStore>((set, get) => ({
  // ─── State ───────────────────────────────────────────────────────────────────
  isOpen: false,
  sessionId: null,
  messages: [],
  isStreaming: false,
  error: null,
  suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,

  // ─── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Toggle the chat panel open/closed
   * Requirement 1.2: Panel open/close state
   */
  togglePanel: () => {
    set((state) => ({ isOpen: !state.isOpen }));
  },

  /**
   * Send a user message and handle the SSE streaming response
   * Requirement 8.1: SSE streaming token reception
   * Requirement 8.5: Input disabled during streaming
   */
  sendMessage: async (content: string) => {
    const { sessionId } = get();

    // Add user message to state
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    // Create placeholder assistant message for streaming
    const assistantMessageId = generateId();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };

    set((state) => ({
      messages: [...state.messages, userMessage, assistantMessage],
      isStreaming: true,
      error: null,
      suggestedQuestions: [],
    }));

    try {
      // Use fetch with ReadableStream since EventSource doesn't support POST
      const token = authService.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'x-tenant-id': getActiveTenantId(),
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${CHAT_API_BASE}/message`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId: sessionId || undefined,
          message: content,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Request failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE lines from the buffer
        const lines = buffer.split('\n');
        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine === '') {
            // Empty line marks end of an event — reset event type
            currentEventType = '';
            continue;
          }

          if (trimmedLine.startsWith('event:')) {
            currentEventType = trimmedLine.slice(6).trim();
            continue;
          }

          if (trimmedLine.startsWith('data:')) {
            const data = trimmedLine.slice(5).trim();
            if (!currentEventType || !data) continue;

            const event = parseSSEEvent(currentEventType, data);
            if (!event) continue;

            switch (event.type) {
              case 'token':
                // Append token content to the assistant message
                set((state) => ({
                  messages: state.messages.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, content: msg.content + event.content }
                      : msg
                  ),
                }));
                break;

              case 'metadata':
                // Attach metadata to the assistant message
                set((state) => ({
                  messages: state.messages.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          metadata: {
                            intent: event.intent,
                            endpointsQueried: event.endpointsQueried,
                            confidenceScore: event.confidenceScore,
                            timeRange: event.timeRange,
                          },
                        }
                      : msg
                  ),
                }));
                break;

              case 'done':
                // Finalize the assistant message and persist sessionId + messages
                try {
                  sessionStorage.setItem(SESSION_STORAGE_KEY, event.sessionId);
                } catch {
                  // sessionStorage may not be available
                }
                set((state) => {
                  const updatedMessages = state.messages.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, id: event.messageId, isStreaming: false }
                      : msg
                  );
                  // Persist messages to sessionStorage for browser-session persistence
                  persistMessagesToSessionStorage(updatedMessages);
                  return {
                    sessionId: event.sessionId,
                    messages: updatedMessages,
                    isStreaming: false,
                  };
                });
                break;

              case 'error':
                // Set error state from SSE error event
                set((state) => ({
                  messages: state.messages.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, isStreaming: false, content: '' }
                      : msg
                  ),
                  isStreaming: false,
                  error: event.message,
                }));
                break;
            }
          }
        }
      }

      // If stream ended without a 'done' event, finalize
      const { isStreaming: stillStreaming } = get();
      if (stillStreaming) {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, isStreaming: false }
              : msg
          ),
          isStreaming: false,
        }));
      }
    } catch (err) {
      // Handle fetch/network errors
      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';

      set((state) => ({
        messages: state.messages.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, isStreaming: false, content: msg.content || '' }
            : msg
        ),
        isStreaming: false,
        error: errorMessage,
      }));
    }
  },

  /**
   * Start a new chat session
   * Clears messages, generates new sessionId, resets suggested questions
   */
  startNewChat: () => {
    // Clear sessionStorage when starting fresh
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(SESSION_MESSAGES_KEY);
    } catch {
      // sessionStorage may not be available
    }
    set({
      sessionId: null,
      messages: [],
      isStreaming: false,
      error: null,
      suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
    });
  },

  /**
   * Archive the current session and start a new one
   * Requirement 7.3: Archive current session and create new empty session
   */
  archiveAndStartNewChat: async () => {
    const { sessionId } = get();

    try {
      // Call backend to create a new session (archives the current one)
      if (sessionId) {
        await chatService.createNewSession();
      }
    } catch {
      // If archiving fails, still allow starting a new chat locally
    }

    // Clear sessionStorage
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(SESSION_MESSAGES_KEY);
    } catch {
      // sessionStorage may not be available
    }

    set({
      sessionId: null,
      messages: [],
      isStreaming: false,
      error: null,
      suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
    });
  },

  /**
   * Restore session from sessionStorage on panel open
   * Requirement 7.2: Session persistence across page navigations
   * First loads from local sessionStorage cache for instant display,
   * then validates with the server for freshness.
   */
  restoreSession: async () => {
    const { sessionId, messages } = get();

    // If we already have messages loaded, don't reload
    if (messages.length > 0 || sessionId) return;

    try {
      const storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);

      // First, try to load cached messages from sessionStorage for instant display
      const cachedMessages = loadMessagesFromSessionStorage();
      if (cachedMessages && cachedMessages.length > 0 && storedSessionId) {
        set({
          sessionId: storedSessionId,
          messages: cachedMessages,
          suggestedQuestions: [],
        });
      }

      // Then validate with the server (refresh if available)
      if (storedSessionId) {
        const restoreToken = authService.getAccessToken();
        const restoreHeaders: Record<string, string> = { 'x-tenant-id': getActiveTenantId() };
        if (restoreToken) {
          restoreHeaders['Authorization'] = `Bearer ${restoreToken}`;
        }
        const response = await fetch(`${CHAT_API_BASE}/sessions/${storedSessionId}/messages`, {
          headers: restoreHeaders,
        });
        if (response.ok) {
          const data = await response.json();
          const serverMessages = data.messages || [];
          if (serverMessages.length > 0) {
            // Update with fresh server data and persist locally
            persistMessagesToSessionStorage(serverMessages);
            set({
              sessionId: storedSessionId,
              messages: serverMessages,
              suggestedQuestions: [],
            });
          }
        } else {
          // Session no longer valid on server, but keep cached messages if available
          if (!cachedMessages || cachedMessages.length === 0) {
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            sessionStorage.removeItem(SESSION_MESSAGES_KEY);
          }
        }
      }
    } catch {
      // If restore fails and no cached data, start fresh
      const cachedMessages = loadMessagesFromSessionStorage();
      if (!cachedMessages || cachedMessages.length === 0) {
        try {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          sessionStorage.removeItem(SESSION_MESSAGES_KEY);
        } catch {
          // sessionStorage may not be available
        }
      }
    }
  },

  /**
   * Load an existing session's messages from the API
   * Requirement 7.2: Session persistence
   */
  loadSession: async (sessionId: string) => {
    set({ error: null });

    try {
      const loadToken = authService.getAccessToken();
      const loadHeaders: Record<string, string> = { 'x-tenant-id': getActiveTenantId() };
      if (loadToken) {
        loadHeaders['Authorization'] = `Bearer ${loadToken}`;
      }
      const response = await fetch(`${CHAT_API_BASE}/sessions/${sessionId}/messages`, {
        headers: loadHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to load session: ${response.status}`);
      }

      const data = await response.json();
      const messages = data.messages || [];

      // Persist to sessionStorage for browser-session persistence
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
      } catch {
        // sessionStorage may not be available
      }
      persistMessagesToSessionStorage(messages);

      set({
        sessionId,
        messages,
        suggestedQuestions: [],
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load session';
      set({ error: errorMessage });
    }
  },

  /**
   * Clear the current error state
   */
  clearError: () => {
    set({ error: null });
  },
}));
